import { createAsyncThunk } from '@reduxjs/toolkit';
import { IDynamicFormValue, toFormFields2 } from 'src/lib';
import { IFilterObj, IGridMetrics } from 'src/components/filters';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import {
  addComment,
  deleteComment,
  deleteCommentsAndOpportunitiesForAllProduct,
  deleteProductCommentsAndOpportunities,
  downloadOpportunitySummary,
  editComment,
  fetchAddSKUFormFields,
  fetchBulkInputsTableRows,
  fetchCogsTableData,
  fetchDynamicDrawerData,
  fetchFilterFieldsForCogs,
  fetchEanZrepTableData,
  fetchGridData,
  fetchLocalFilters,
  fetchOpportunityForBulkInputsTable,
  fetchOpportunityForSummary,
  fetchOpportunitySummaryDetails,
  fetchOpportunitySummaryTableData,
  fetchPlannerToggleData,
  fetchProductsWithComments,
  resetCogsToDefault,
  // fetchTableDataForCogs,
  saveScenarioPlanner,
  submitCogsTableData,
  submitBulkComment,
  submitBulkOpportunities,
  submitDrawerOpportunities,
  fetchPHRecommendations
} from '../api/planner-api';
import { IAddCommentPayload } from '../types/drawer-view-type';
import { IOpportunityPayload } from '../types/opportunity.model';
import getLocalFiltersPayload from '../utils/utils';
import { IBulkCommentsPayload } from '../types/bulk-inputs-table-data.model';
import { IPlannerState } from '../types/planner-state.model';
import { IFilterState } from '../../common';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import { TypeOfTableData } from '../../pricing-simulator/types/generic-table.model';
import { SummaryOpportunity } from '../data/summary-opportunity';

interface IDrawerAsyncThunkArgs {
  apiPayload: {
    toggle: string;
  };
  urlParams: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  };
}

// Interface defining the structure of the arguments required by the ean zrep table data async thunk
interface IEanZrepAsyncThunkArgs {
  apiPayload: {}; //payload to be sent with the API request
  urlParams: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  };
  productName: string; //product name of the product related to the request
}

interface IAddCommentAsyncThunkArgs extends IAddCommentPayload {
  scenarioId: number;
  sourceId: number;
  moduleId: number;
}

interface IUpdateCommentAsyncThunkArgs {
  commentId: number;
  commentText: string;
}

function getGlobalFilterValuesFromPlanner(thunkAPI: {
  getState: () => unknown;
}) {
  const {
    globalFilterValue,
    globalFilterCombinations,
    globalFilterFormFields
  } = (thunkAPI.getState() as unknown as any).filters as IFilterState;
  return transformDynamicFormValueToFilterPayload({
    formValue: globalFilterValue,
    formFields: globalFilterFormFields,
    combinations: globalFilterCombinations
  });
}

function getLocalFilterValuesFromPlanner(thunkAPI: {
  getState: () => unknown;
}) {
  const { localFilterFormFields, localFilterValue } = (
    thunkAPI.getState() as unknown as any
  ).plannerV2 as IPlannerState;
  return getLocalFiltersPayload(localFilterValue, localFilterFormFields);
}

export const getGridData = createAsyncThunk(
  '/api/planner/getGridData',
  async (
    payload: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
      localFilters: IDynamicFormValue;
      filters: IFilterObj[];
    },
    thunkAPI
  ) => {
    const { gridCellMetadata } = (thunkAPI.getState() as any)
      .plannerV2 as IPlannerState;
    const gridCellMetadataResponse = gridCellMetadata.length
      ? { data: gridCellMetadata }
      : await fetchPlannerToggleData(payload.sourceId, 'cell');
    const response = await fetchGridData(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      payload.filters,
      payload.localFilters
    );
    return {
      gridCellMetadata: gridCellMetadataResponse.data,
      gridMatrix: response.data
    };
  }
);

export const getOpportunitySummaryTableDataV2 = createAsyncThunk(
  '/api/planner/getOpportunitySummaryTableDataV2',
  async (opportunitySummaryPayload: {
    filters: IFilterObj[];
    toggle: string;
    localFilters: IGridMetrics;
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const { filters, toggle, localFilters, moduleId, scenarioId, sourceId } =
      opportunitySummaryPayload;
    const response = await fetchOpportunitySummaryTableData(
      {
        toggle,
        local_filters: localFilters,
        filter_inputs: filters
      },
      sourceId,
      moduleId,
      scenarioId
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.OPPORTUNITY_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      tableData: response,
      inputCols
    };
  }
);

export const getDynamicDrawerData = createAsyncThunk(
  '/api/planner/getDynamicDrawerData',
  async (payload: IDrawerAsyncThunkArgs, thunkAPI) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const { moduleId, scenarioId, sourceId } = payload.urlParams;
    const response = await fetchDynamicDrawerData(
      {
        ...payload.apiPayload,
        filter_inputs: filters
      },
      sourceId,
      moduleId,
      scenarioId
    );
    return response.data;
  }
);

// Async thunk action to fetch EanZrep table data based on provided arguments
export const getEanZrepTableData = createAsyncThunk(
  '/api/planner/getEanZrepTableData', //thunk name
  async (payload: IEanZrepAsyncThunkArgs,thunkAPI) => {
    // Destructure moduleId, scenarioId, and sourceId from the payload's urlParams
    const { moduleId, scenarioId, sourceId } = payload.urlParams;

    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    // make the API request to fetch Ean Zrep table data using the provided parameters
    const response = await fetchEanZrepTableData(
      { ...payload.apiPayload,filters },
      sourceId,
      moduleId,
      scenarioId
    );
    // return the response data along with the product name
    return { data: response, productName: payload.productName };
  }
);

export const addNewComment = createAsyncThunk(
  'addNewComment',
  async (payload: IAddCommentAsyncThunkArgs, thunkAPI) => {
    const response = await addComment(
      {
        ...payload.params.productMapping,
        comment_text: payload.params.comment_text,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      },
      payload.sourceId,
      payload.moduleId,
      payload.scenarioId
    );
    return response.data;
  }
);

export const updateComment = createAsyncThunk(
  'updateComment',
  async (payload: IUpdateCommentAsyncThunkArgs) => {
    const response = await editComment(payload);
    return response.data;
  }
);

export const deleteCommentById = createAsyncThunk(
  'deleteCommentById',
  async (commentId: number) => {
    const response = await deleteComment(commentId);
    return response;
  }
);

export const submitOpportunitiesForProduct = createAsyncThunk(
  '/api/planner/submitOpportunitiesForProduct',
  async (
    arg: {
      payload: { [key: string]: string }[];
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const { moduleId, payload, sourceId, scenarioId } = arg;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    await submitDrawerOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      payload,
      filters
    );
    return payload;
  }
);

export const resetDrawerDetails = createAsyncThunk(
  '/api/planner/resetDrawerDetails',
  async (
    arg: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
      payload: {
        id: number;
        /**
         * Will be undefined when we want the entire PPG and its SKUs opportunities to be reset
         */
        key: string;
        resetAll: boolean;
        /**
         * Drawer toggle
         */
      };
    },
    thunkAPI
  ) => {
    const { moduleId, scenarioId, sourceId, payload } = arg;
    await deleteProductCommentsAndOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      {
        id: payload.id,
        key: payload.key,
        reset_all: payload.resetAll,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      }
    );
    return payload.resetAll;
  }
);

export const resetAllOpportunities = createAsyncThunk(
  '/api/planner/resetAll',
  async (
    arg: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
      prodDetails: { ppg_id: number; sku_id: number }[];
      resetAll: boolean;
      /**
       * View of grid
       */
      toggle: string;
    },
    thunkAPI
  ) => {
    const { moduleId, resetAll, scenarioId, prodDetails, sourceId, toggle } =
      arg;
    await deleteCommentsAndOpportunitiesForAllProduct(
      sourceId,
      moduleId,
      scenarioId,
      {
        prod_details: prodDetails,
        reset_all: resetAll,
        toggle,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      }
    );
    return { resetAll };
  }
);

export const submitOpportunitiesForProductsInBulk = createAsyncThunk(
  '/api/planner/submitOpportunitiesForProductsInBulk',
  async (
    arg: {
      payload: IOpportunityPayload[];
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const { moduleId, payload, sourceId, scenarioId } = arg;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    await submitBulkOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      payload,
      filters
    );
    return payload;
  }
);

export const addBulkCommentsByOpportunity = createAsyncThunk(
  '/api/planner/addBulkCommentsByOpportunity',
  async (
    args: {
      scenarioId: number;
      comment: string;
      products: IBulkCommentsPayload['prod_details'];
    },
    thunkAPI
  ) => {
    const { scenarioId, comment, products } = args;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const response = await submitBulkComment(scenarioId, {
      comment_text: comment,
      filter_inputs: filters,
      prod_details: products
    });
    return response;
  }
);

export const getProductsWithComments = createAsyncThunk(
  '/api/planner/getProductsWithComments',
  async (
    payload: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchProductsWithComments(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      filters,
      localFilters
    );
    return response.data;
  }
);

export const getOpportunitySummaryTableData = createAsyncThunk(
  '/api/planner/getOpportunitySummaryData',
  async (
    payload: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchOpportunitySummaryDetails(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      filters,
      localFilters
    );
    return response.data;
  }
);

export const getAddSKUFormFields = createAsyncThunk(
  '/api/planner/getAddSKUFormFields',
  async (payload: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response = await fetchAddSKUFormFields(
      payload.sourceId,
      payload.scenarioId,
      payload.moduleId,
      payload.filters
    );
    return {
      fields: toFormFields2(response.fields),
      combinations: response.combinations
    };
  }
);

export const getLocalFiltersData = createAsyncThunk(
  'getLocalFiltersData',
  async (
    args: { sourceID: number; scenarioId: number; filters: IFilterObj[] },
    thunkAPI
  ) => {
    const response = await fetchLocalFilters(
      args.sourceID,
      args.scenarioId,
      args.filters
    );
    const fields = toFormFields2(response.localFilter);
    const values = fields.reduce<IDynamicFormValue>(
      (prev, curr) => ({ ...prev, [curr.name]: curr.default_value }),
      {}
    );
    return { values, fields };
  }
);

// function for following and unfollowing ph
export const getPHRecommendations = createAsyncThunk(
  'getPHRecommendations',
  async (
    payload: {
      sourceId: number;
      scenarioId: number;
      moduleId: number;
      phFollow: boolean;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchPHRecommendations(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      filters,
      localFilters,
      payload.phFollow
    );
    return response.data;
  }
);

export const getFilterFieldsForCogs = createAsyncThunk(
  'getFilterFieldsForCogs',
  async (
    args: {
      sourceID: number;
      scenarioId: number;
      moduleId: number;
    },
    thunkAPI
  ) => {
    const response = await fetchFilterFieldsForCogs(
      args.sourceID,
      args.moduleId,
      args.scenarioId
    );
    return response;
  }
);

export const resetCogsSimulationData = createAsyncThunk(
  'resetCogsSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId,
    filters
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response = await resetCogsToDefault(
      sourceId,
      moduleId,
      scenarioId,
      filters
    );
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],

      response.header,

      ''
    );

    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );

    return {
      resultCols,
      inputCols,
      response
    };
  }
);

export const getTableDataForCogs = createAsyncThunk(
  'getTableDataForCogs',
  async ({
    dataInput,
    sourceId,
    moduleId,
    scenarioId
  }: {
    dataInput: any;
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const isSimulation =
      (dataInput.row && dataInput.row.data && dataInput.row.data.length) ||
      (dataInput.row && dataInput.row.inputs && dataInput.row.inputs.length);

    const response = isSimulation
      ? await submitCogsTableData(dataInput, sourceId, moduleId, scenarioId)
      : await fetchCogsTableData(dataInput, sourceId, moduleId, scenarioId);
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols,
      isSimulation
    };
  }
);

export const savePlanner = createAsyncThunk(
  '/api/planner/savePlanner',
  async (scenarioId: number, thunkAPI) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await saveScenarioPlanner(
      scenarioId,
      filters,
      localFilters
    );
    return response;
  }
);

export const getSummaryTableOpportunity = createAsyncThunk(
  '/api/home/getSummaryTableOpportunity',
  async (sourceId: number) => {
    const response = await fetchOpportunityForSummary(sourceId);
    const tableData = SummaryOpportunity;
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.OPPORTUNITY_FLAG,
        TypeOfTableData.INPUT
      ],
      tableData.header,
      ''
    );
    return {
      response: response.opportunities,
      tableData: SummaryOpportunity,
      inputCols
    };
  }
);

export const getBulkInputsTableOpportunity = createAsyncThunk(
  '/api/home/getBulkInputsTableOpportunity',
  async (sourceId: number) => {
    const response = await fetchOpportunityForBulkInputsTable(sourceId);
    return response.opportunities;
  }
);

export const downloadSummary = createAsyncThunk(
  '/api/planner/savePlanner',
  async (
    args: {
      scenarioId: number;
      moduleId: number;
      sourceId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const scenarioName =
      (thunkAPI.getState() as any)?.simulatorHome?.currentScenario?.name ||
      'Summary';
    const response = await downloadOpportunitySummary(
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      scenarioName,
      filters,
      localFilters
    );
    return response;
  }
);

export const getBulkInputsTableRows = createAsyncThunk(
  '/api/planner/getBulkInputsTableRows',
  async (
    args: {
      scenarioId: number;
      moduleId: number;
      sourceId: number;
      toggleView: string;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchBulkInputsTableRows(
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      filters,
      localFilters,
      args.toggleView
    );
    return response.data;
  }
);

/*
 ** API to get fetch the details of planner toggle
 */
export const getPlannerToggles = createAsyncThunk(
  '/api/planner/getToggleData',
  async (args: { sourceId: number }) => {
    const response = await fetchPlannerToggleData(args.sourceId, 'drawer');
    return response.data;
  }
);
